import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import { format, isSunday, previousSunday, subWeeks } from 'date-fns'

import {
  Box,
  Button,
  ButtonSet,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Text,
} from '@indeed/ifl-components'

import { downloadAsFileFromApi } from 'syft-acp-core/api/file'
import DataFreeDropdown from 'syft-acp-core/components/EditableTable/DataDropdown'
import { hideModal } from 'syft-acp-core/store/modals/actions'
import { getModal$ } from 'syft-acp-core/store/modals/selectors'
import { withTrackingTriggerHoc } from 'syft-acp-util/withTrackingTriggerHoc'

import { trackingEvents } from './RetrospectiveAgencyHoursModal.tracking'
import { RetrospectiveAgencyHoursModalProps as Props } from './RetrospectiveAgencyHoursModal.types'

export const RetrospectiveAgencyHoursModal = ({ dispatch, item, triggerEvent }: Props) => {
  const DATE_FORMAT = 'yyyy-MM-dd'
  const WEEKS_LOOK_BACK = 26

  const { locale } = useIntl()
  const [selectedSunday, setSelectedSunday] = useState<string | null>(null)
  const [sundays, setSundays] = useState<Array<{ id: string; label: string }>>([])

  const displayFormat = locale === 'en-GB' ? 'dd/MM/yyyy' : 'MM/dd/yyyy'

  useEffect(() => {
    if (item.isShown) {
      // Generate Sundays for the last ~6 months (26 weeks)
      // and use them to populate the dropdown.
      // Default to the most recent Sunday.
      const today = new Date()
      const mostRecentSunday = isSunday(today) ? today : previousSunday(today)
      const sundayOptions = []
      for (let i = 0; i < WEEKS_LOOK_BACK; i++) {
        const sunday = subWeeks(mostRecentSunday, i)
        sundayOptions.push({
          id: format(sunday, DATE_FORMAT),
          label: format(sunday, displayFormat),
        })
      }

      setSelectedSunday(format(mostRecentSunday, DATE_FORMAT))
      setSundays(sundayOptions)
    }
  }, [item.isShown, displayFormat])

  const closeModal = () => dispatch(hideModal(item.modalName))

  const fetchRetrospectiveAgencyHours = () => {
    return downloadAsFileFromApi('/admin/retrospective_agency_shift_workers.csv', {
      invoicing_end_date: selectedSunday,
      strategy: 'agency_retro_billing_items',
    })
  }

  const handleOnExit = () => {
    triggerEvent(trackingEvents.CLOSE_BUTTON.CLICKED)
    closeModal()
  }

  const handleOnChange = (sundayDate: string | null) => {
    if (sundayDate) {
      triggerEvent(trackingEvents.DATE.CHANGED, { day: sundayDate })
      setSelectedSunday(sundayDate)
    }
  }

  const handleCancel = () => {
    triggerEvent(trackingEvents.CANCEL_BUTTON.CLICKED)
    closeModal()
  }

  const handleDownload = () => {
    fetchRetrospectiveAgencyHours()
    triggerEvent(trackingEvents.DOWNLOAD_BUTTON.CLICKED, { day: selectedSunday })
    closeModal()
  }

  return (
    <Dialog open={item.isShown} onOpenChange={() => handleOnExit()}>
      <DialogContent>
        <DialogHeader>Download Retrospective agency hours CSV</DialogHeader>
        <DialogBody sx={{ textAlign: 'left' }}>
          <Text as="p" level={3}>
            Invoicing end date
          </Text>
          <Box sx={{ height: 50 }}>
            <Box sx={{ position: 'fixed', width: '550px', zIndex: 1000 }}>
              <DataFreeDropdown
                name="Invoicing end date"
                header="Select date"
                value={selectedSunday || ''}
                editable={true}
                options={sundays}
                onChange={value => handleOnChange(value)}
                parameters={{ convertIds: false }}
              />
            </Box>
          </Box>
        </DialogBody>
        <DialogFooter>
          <ButtonSet>
            <Button onClick={handleCancel} variant="secondary">
              Cancel
            </Button>
            <Button onClick={handleDownload}>Download</Button>
          </ButtonSet>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default connect(state => ({
  item: getModal$(state.modals)('retrospectiveAgencyHoursModal'),
}))(withTrackingTriggerHoc(RetrospectiveAgencyHoursModal))
